import { useState, useContext, useEffect } from "react";
import dayjs from "dayjs";
import { Loader, SquareArrowOutUpRight } from "lucide-react";
import api from "apiSingleton";
import { AlertContext } from "context/providers";
import { cn } from "utils";
import {
  formatIntegrityRisk,
  getIntegrityRisk,
  getIntegrityRiskStyles,
} from "utils/helpers/assessments";
import AssessmentReportLink from "components/AssessmentReportLink";
import * as RootDialog from "components/ui/dialog";
import AssessmentReport from "components/AssessmentReport";
import {
  PROFILE_SECTION_TITLE_CLASSNAME,
  PROFILE_MORE_LINK_CLASSNAME,
} from "../constants";
import { ProfileMoreLinkLabel } from "../ProfileMoreLinkLabel";

function CollapsibleAssessments({ assessments: initialAssessments, hit }) {
  const [seeAssessments, setSeeAssessments] = useState(false);
  const [sentAssessments, setSentAssessments] = useState(null);
  const [assessments, setAssessments] = useState(initialAssessments || []);
  const [loading, setLoading] = useState(false);
  const { addGraphQLAlert } = useContext(AlertContext);

  const userId = hit.id;

  const fetchAssessments = async () => {
    try {
      setLoading(true);
      const [sharedData, userData] = await Promise.all([
        api.user.getSharedAssessments({ userId }),
        api.user.getAssessments({ id: userId }),
      ]);
      const fetchedAssessments = userData?.data?.getUser?.assessments || [];
      setAssessments(fetchedAssessments);
      const assessmentsNotCompleted = (
        sharedData?.data?.listSharedAssessmentsByUser?.items || []
      ).filter((a) => !fetchedAssessments.find((b) => b.id === a.id));
      setSentAssessments(assessmentsNotCompleted);
    } catch (err) {
      console.log("Something went wrong getting user assessments", err);
      addGraphQLAlert(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssessments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const assessmentsLength =
    (assessments?.length ?? 0) + (sentAssessments?.length ?? 0);

  return (
    <section className="talent-card-profile-section">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className={PROFILE_SECTION_TITLE_CLASSNAME}>Assessments</h3>
      </div>
      {loading ? (
        <div className="flex justify-center items-center w-full h-8 animate-spin">
          <Loader />
        </div>
      ) : (
        <>
          <Assessments
            items={
              seeAssessments
                ? assessments || []
                : assessments?.slice(0, 3) || []
            }
            hit={hit}
          />
          {assessments.length > 3 && (
            <button
              type="button"
              className={PROFILE_MORE_LINK_CLASSNAME}
              onClick={() => setSeeAssessments((prev) => !prev)}
            >
              {seeAssessments ? (
                "Less"
              ) : (
                <ProfileMoreLinkLabel count={assessments.length - 3} />
              )}
            </button>
          )}
          {(sentAssessments?.length || 0) > 0 && (
            <div>
              <p className="font-semibold mt-5">Pending assessments:</p>
              {sentAssessments.map((a) => (
                <p className="my-2 text-sm" key={a.id}>
                  {a.testName}
                </p>
              ))}
            </div>
          )}
          {!assessmentsLength && <p>No assessments taken or sent.</p>}
        </>
      )}
    </section>
  );
}

function Assessments({ items, hit }) {
  const prepareScore = (item) => {
    return `${item.finalScore || 0} (Code - ${item.codeScore || 0}, MCQ - ${
      item.multipleChoiceScore || 0
    })`;
  };

  const formatStatus = (status) => {
    if (!status) {
      return "-";
    }

    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  const formatDuration = (durationMinutes) => {
    if (!durationMinutes) {
      return "-";
    }

    const totalMinutes = Math.max(Math.floor(durationMinutes), 1);

    if (totalMinutes < 60) {
      return `${totalMinutes} mins`;
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
  };

  const formatDate = (assessment) => {
    const date =
      assessment.assessmentCompleted ||
      assessment.updatedAt ||
      assessment.createdAt;

    if (!date) {
      return "-";
    }

    const parsedDate = dayjs(date);

    return parsedDate.isValid() ? parsedDate.format("MM/DD/YYYY") : "-";
  };

  // A missing rating renders as a hyphen rather than as a reassuring value -
  // an assessment that was never rated is not the same as one rated clean.
  const renderIntegrityRisk = (assessment) => {
    const risk = getIntegrityRisk(assessment);

    if (!risk) {
      return "-";
    }

    return (
      <span
        className={cn(
          "inline-flex whitespace-nowrap rounded border px-2 py-0.5 text-xs font-bold uppercase",
          getIntegrityRiskStyles(risk)
        )}
      >
        {formatIntegrityRisk(risk)}
      </span>
    );
  };

  const cellClassName = "min-w-0 truncate px-3 py-3";
  // The header and each body row are separate grid containers, so every track
  // has to size the same way in all of them or the columns stop lining up -
  // which rules out content-based sizing (max-content resolves against each
  // container's own content). The six trailing columns are therefore fixed at
  // roughly what their widest value needs, including the integrity badge, and
  // Test takes the remainder. They total ~644px, so the table fits the card at
  // ordinary widths rather than forcing a horizontal scroll.
  const gridClassName =
    "grid min-w-0 grid-cols-[minmax(120px,1fr)_100px_72px_140px_84px_108px_140px] items-center";

  if (!items.length) {
    return null;
  }

  return (
    <div className="w-full overflow-x-auto" data-testid="assessments-table">
      <div
        className={cn(
          gridClassName,
          "mb-2 rounded-lg bg-slate-100 text-sm font-semibold text-slate-700"
        )}
      >
        <div className={cellClassName}>Test</div>
        <div className={cellClassName}>Status</div>
        <div className={cellClassName}>Score</div>
        <div className={cellClassName}>Integrity</div>
        <div className={cellClassName}>Time</div>
        <div className={cellClassName}>Date</div>
        <div className={cellClassName} />
      </div>

      <div className="space-y-2">
        {items.map((assessment) => {
          const hasReportDetails = assessment.resultDetails?.analysis;

          const pdfHref = assessment.pdfResultLocation
            ? `https://res.cloudinary.com/${
                process.env.REACT_APP_CLOUDINARY_CLOUDNAME
              }/image/upload/v${Date.now()}/${assessment.pdfResultLocation}.pdf`
            : assessment.reportLink || "";

          return (
            <div
              className={cn(
                gridClassName,
                "rounded-lg bg-slate-50 text-sm text-slate-600"
              )}
              key={assessment.id}
              data-testid="assessment-row"
            >
              <div
                className={cn(cellClassName, "font-medium text-slate-700")}
                title={assessment.testName}
              >
                {assessment.testName || "-"}
              </div>
              <div
                className={cellClassName}
                title={
                  assessment.status
                    ? formatStatus(assessment.status)
                    : undefined
                }
              >
                {formatStatus(assessment.status)}
              </div>
              <div
                className={cellClassName}
                title={prepareScore(assessment)}
                data-testid="assessment-score"
              >
                {assessment.finalScore ?? "-"}
              </div>
              <div
                className={cellClassName}
                title={formatIntegrityRisk(getIntegrityRisk(assessment))}
                data-testid="assessment-integrity"
              >
                {renderIntegrityRisk(assessment)}
              </div>
              <div className={cellClassName}>
                {formatDuration(assessment.timeTaken)}
              </div>
              <div className={cellClassName}>{formatDate(assessment)}</div>
              <div className={cn(cellClassName, "flex justify-end")}>
                {hasReportDetails ? (
                  <RootDialog.Dialog>
                    <RootDialog.DialogTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex items-center gap-2 whitespace-nowrap font-semibold text-slate-700 transition hover:text-sky-600"
                      >
                        View details
                      </button>
                    </RootDialog.DialogTrigger>

                    <RootDialog.DialogPortal>
                      <RootDialog.DialogOverlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm animate-in fade-in duration-300" />

                      <RootDialog.DialogContent className="z-50 flex max-h-[90vh] w-[95vw] max-w-6xl flex-col bg-slate-50 shadow-2xl md:w-[90%]">
                        <div className="z-20 flex flex-shrink-0 flex-row items-center justify-between border-b border-slate-100 p-4 px-6">
                          <div className="flex flex-col">
                            <RootDialog.DialogTitle className="text-base font-bold uppercase tracking-wider text-slate-900">
                              Assessment Overview
                            </RootDialog.DialogTitle>
                            <RootDialog.DialogDescription className="mt-1 text-sm text-slate-500">
                              {assessment.testName}
                            </RootDialog.DialogDescription>
                          </div>
                        </div>

                        <AssessmentReport assessment={assessment} hit={hit} />
                      </RootDialog.DialogContent>
                    </RootDialog.DialogPortal>
                  </RootDialog.Dialog>
                ) : assessment.reportLink ? (
                  <AssessmentReportLink
                    assessment={assessment}
                    userId={hit.id}
                    className="inline-flex items-center gap-2 whitespace-nowrap font-semibold text-slate-700 transition hover:text-sky-600"
                  >
                    View details
                    <SquareArrowOutUpRight className="h-4 w-4" />
                  </AssessmentReportLink>
                ) : pdfHref ? (
                  <a
                    href={pdfHref}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 whitespace-nowrap font-semibold text-slate-700 transition hover:text-sky-600"
                  >
                    View details
                    <SquareArrowOutUpRight className="h-4 w-4" />
                  </a>
                ) : (
                  <span>-</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default CollapsibleAssessments;
